import { html, LitElement, nothing } from 'lit';
import Styles from './vm-client-disclosure.scss';
import { EVENT_PLAYER_CHAPTER_CHANGE, EVENT_CLIENT_DISCLOSURE_RESOLVED } from 'Utils/types';
import * as Sentry from '@sentry/browser';
import { buildURL, fetchRequest } from 'Utils/api-fetch-request';

let fetchPromise = null;
let eventDispatched = false;
let lastShownKey = undefined;

// Module-level function so the fetch is shared across multiple component instances (mobile/desktop)
function fetchDisclosureData(videoId, lectureSeriesId, forceAdDisclosure) {
    let params;
    if (forceAdDisclosure) {
        params = { force_ad_disclosure: forceAdDisclosure };
    } else {
        params = lectureSeriesId ? { lecture_series_id: lectureSeriesId } : { video_id: videoId };
    }
    if (!fetchPromise) {
        const url = buildURL('/api/ad-disclosure/', params);
        fetchPromise = fetchRequest(url);
    }
    return fetchPromise;
}

export default class VmClientDisclosure extends LitElement {
    static properties = {
        videoId: { type: String, attribute: 'video-id' },
        lectureSeriesId: { type: String, attribute: 'lecture-series-id' },
        userId: { type: String, attribute: 'user-id' },
        disclosureItems: { type: Array, state: true },
        dataFetched: { type: Boolean, state: true },
        fetchError: { type: Boolean, state: true },
        activeChapterId: { type: Number, state: true },
    };

    static styles = Styles;

    get globalDisclosureItem() {
        return this.disclosureItems.find((item) => !item.chapter_id);
    }

    get chapterDisclosureItems() {
        return this.disclosureItems.filter((item) => item.chapter_id);
    }

    get activeChapterItem() {
        return this.chapterDisclosureItems.find((item) => item.chapter_id === this.activeChapterId);
    }

    get activeDisclosureItem() {
        return this.globalDisclosureItem || this.activeChapterItem;
    }

    constructor() {
        super();
        this.videoId = '';
        this.lectureSeriesId = '';
        this.userId = '';
        this.disclosureItems = [];
        this.dataFetched = false;
        this.fetchError = false;
        this.forceAdDisclosure = new URLSearchParams(window.location.search).get('force_ad_disclosure');
    }

    trackMixpanelEvent(eventName) {
        const item = this.activeDisclosureItem;

        if (!item || !window.vumedi?.mixpanel || this.forceAdDisclosure) return false;

        const props = {
            ad_disclosure_id: item.id,
            ad_disclosure_name: item.name,
        };

        if (this.lectureSeriesId) {
            props.lecture_series_id = Number(this.lectureSeriesId);
        }

        if (this.videoId) {
            props.video_id = Number(this.videoId);
        }

        if (this.activeChapterItem) {
            props.chapter_id = Number(this.activeChapterItem.chapter_id);
        }

        window.vumedi.mixpanel.track(eventName, props);

        return true;
    }

    // Module-level guard ensures only one instance reports per appearance
    trackShownEvent() {
        if (!this.activeDisclosureItem) {
            lastShownKey = undefined;
            return;
        }

        // Use chapter ID for chapter disclosures, 'global' for global disclosure
        // global fires once,
        // chapter disclosures fire per chapter change, even if the same disclosure is shown
        const key = this.globalDisclosureItem ? 'global' : this.activeChapterId;
        if (lastShownKey === key) return;

        // Only set the key if the event was actually sent
        if (this.trackMixpanelEvent('AdDisclosureShown')) {
            lastShownKey = key;
        }
    }

    async connectedCallback() {
        super.connectedCallback();

        if (!this.userId) {
            window.dispatchEvent(new CustomEvent(EVENT_CLIENT_DISCLOSURE_RESOLVED, { detail: [] }));

            return;
        }

        this.handleChapterChange = (e) => {
            this.activeChapterId = Number(e.detail.chapterId);
        };

        window.addEventListener(EVENT_PLAYER_CHAPTER_CHANGE, this.handleChapterChange);

        try {
            this.disclosureItems = await fetchDisclosureData(
                this.videoId,
                this.lectureSeriesId,
                this.forceAdDisclosure,
            );
        } catch (error) {
            Sentry.captureException(error);
            this.fetchError = true;
        }

        if (!eventDispatched) {
            eventDispatched = true;
            if (this.fetchError || !this.globalDisclosureItem) {
                const chapterIdsForExclusion = this.chapterDisclosureItems.map((item) => item.chapter_id);
                window.dispatchEvent(
                    new CustomEvent(EVENT_CLIENT_DISCLOSURE_RESOLVED, { detail: chapterIdsForExclusion }),
                );
            }
        }

        this.dataFetched = true;
    }

    updated() {
        this.classList.toggle('is-hidden', !this.globalDisclosureItem && !this.activeChapterItem);
        if (this.dataFetched) this.classList.add('is-resolved');

        this.trackShownEvent();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener(EVENT_PLAYER_CHAPTER_CHANGE, this.handleChapterChange);
    }

    renderDisclosure(item) {
        return html`
            <div
                class="vm-client-disclosure__content-wrap"
                @click=${() => this.trackMixpanelEvent('AdDisclosureClicked')}
                @mouseenter=${() =>
                    window.matchMedia('(hover: hover)').matches && this.trackMixpanelEvent('AdDisclosureHovered')}
            >
                <div class="vm-client-disclosure__logo-wrap">
                    <figure class="vm-client-disclosure__logo">
                        <img src="${item.logo}" alt="${item.name}" />
                        <figcaption class="vm-client-disclosure__logo-caption">Advertisement</figcaption>
                    </figure>
                </div>
                <div class="vm-client-disclosure__text-wrap">
                    <p class="vm-client-disclosure__text">Content developed independently by authors.</p>
                </div>
            </div>
        `;
    }

    render() {
        if (!this.dataFetched) return nothing;

        if (this.globalDisclosureItem) {
            return this.renderDisclosure(this.globalDisclosureItem);
        }

        if (this.activeChapterItem) {
            return this.renderDisclosure(this.activeChapterItem);
        }

        return nothing;
    }
}
